<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)
import {
  useVueTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  FlexRender,
  type SortingState,
} from '@tanstack/vue-table'
import { useVirtualizer } from '@tanstack/vue-virtual'

interface Tracking {
  id: number
  created_at: string
  user: string
  action: string
  composer: string | null
  album: string | null
  title: string
  artist: string
  siteName: string
}

const props = defineProps<{
  apiUrl: string
  canEdit: boolean
}>()

const data = ref<Tracking[]>([])
const loading = ref(true)
const loadingMore = ref(false)
const error = ref<string | null>(null)
const globalFilter = ref('')
const sorting = ref<SortingState>([])
const startDate = ref('')
const endDate = ref('')
const totalCount = ref(0)
const hasMore = ref(false)
const currentOffset = ref(0)
const pageSize = 100

const tableContainerRef = ref<HTMLElement | null>(null)

// Filters
const hideShoplanet = ref(false)

// Charts
const showBarChart = ref(false)
const showLineChart = ref(false)
const showPieChart = ref(false)
const showHelpModal = ref(false)
const barChartRef = ref<HTMLCanvasElement | null>(null)
const lineChartRef = ref<HTMLCanvasElement | null>(null)
const pieChartRef = ref<HTMLCanvasElement | null>(null)
let barChartInstance: Chart | null = null
let lineChartInstance: Chart | null = null
let pieChartInstance: Chart | null = null
let autoRefreshInterval: ReturnType<typeof setInterval> | null = null
const AUTO_REFRESH_INTERVAL = 5000 // 5 seconds

const columnHelper = createColumnHelper<Tracking>()

const columns = [
  columnHelper.accessor('created_at', {
    header: 'When',
    cell: (info) => formatDate(info.getValue()),
    size: 160,
  }),
  columnHelper.accessor('user', {
    header: 'User',
    size: 120,
  }),
  columnHelper.accessor('action', {
    header: 'Action',
    size: 80,
  }),
  columnHelper.accessor('composer', {
    header: 'Composer',
    cell: (info) => decodeHtmlEntities(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor('album', {
    header: 'Album',
    cell: (info) => decodeHtmlEntities(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor('title', {
    header: 'Title',
    cell: (info) => decodeHtmlEntities(info.getValue()),
    size: 200,
  }),
  columnHelper.accessor('artist', {
    header: 'Artist',
    cell: (info) => decodeHtmlEntities(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor('siteName', {
    header: 'Site',
    size: 120,
  }),
]

if (props.canEdit) {
  columns.push(
    columnHelper.display({
      id: 'actions',
      header: 'Edit',
      size: 60,
      cell: (info) => info.row.original.id,
    })
  )
}

// Combined filter value that triggers re-filtering when either changes
const combinedFilter = computed(() => ({
  search: globalFilter.value,
  hideShoplanet: hideShoplanet.value
}))

const table = useVueTable({
  get data() {
    return data.value
  },
  columns,
  state: {
    get sorting() {
      return sorting.value
    },
    get globalFilter() {
      return combinedFilter.value
    },
  },
  onSortingChange: (updater) => {
    sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater
  },
  globalFilterFn: (row, _columnId, filterValue) => {
    // First check hideShoplanet filter
    if (filterValue?.hideShoplanet) {
      const user = row.original.user?.toLowerCase() || ''
      if (user.includes('shoplanet')) {
        return false
      }
    }
    // Then apply text search filter
    const search = filterValue?.search
    if (!search) return true
    const searchLower = search.toLowerCase()
    return Object.values(row.original).some(val =>
      String(val).toLowerCase().includes(searchLower)
    )
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
})

const rowVirtualizerOptions = computed(() => ({
  count: table.getRowModel().rows.length,
  getScrollElement: () => tableContainerRef.value,
  estimateSize: () => 40,
  overscan: 10,
}))

const rowVirtualizer = useVirtualizer(rowVirtualizerOptions)

const virtualRows = computed(() => {
  return rowVirtualizer.value.getVirtualItems()
})

const totalSize = computed(() => {
  return rowVirtualizer.value.getTotalSize()
})

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function decodeHtmlEntities(str: string | null): string {
  if (!str) return '-'
  return str
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&ndash;/g, '–')
    .replace(/&mdash;/g, '—')
}

async function fetchData(append = false) {
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
    currentOffset.value = 0
    data.value = []
  }
  error.value = null

  try {
    const params = new URLSearchParams()
    params.append('limit', String(pageSize))
    params.append('offset', String(currentOffset.value))

    if (startDate.value) params.append('startDate', startDate.value)
    if (endDate.value) params.append('endDate', endDate.value)

    const url = `${props.apiUrl}.json?${params.toString()}`

    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const json = await response.json()
    const newTrackings = json.trackings || json

    if (append) {
      data.value = [...data.value, ...newTrackings]
    } else {
      data.value = newTrackings
    }

    totalCount.value = json.total_count || data.value.length
    hasMore.value = json.has_more || false
    currentOffset.value += newTrackings.length
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch data'
    console.error('Fetch error:', e)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

function handleDateFilter() {
  fetchData()
}

function loadMore() {
  if (!loadingMore.value && hasMore.value) {
    fetchData(true)
  }
}

function handleScroll(event: Event) {
  const target = event.target as HTMLElement
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight
  // Load more when within 200px of the bottom
  if (scrollBottom < 200 && !loadingMore.value && hasMore.value) {
    loadMore()
  }
}

// Fetch only new records (for auto-refresh)
async function fetchNewRecords() {
  // Don't fetch if currently loading or if there's no data yet
  if (loading.value || loadingMore.value || data.value.length === 0) return

  try {
    // Get the most recent record's ID
    const latestId = Math.max(...data.value.map(t => t.id))

    const params = new URLSearchParams()
    params.append('since_id', String(latestId))
    if (startDate.value) params.append('startDate', startDate.value)
    if (endDate.value) params.append('endDate', endDate.value)

    const url = `${props.apiUrl}.json?${params.toString()}`

    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'same-origin',
    })

    if (!response.ok) return

    const json = await response.json()
    const newTrackings = json.trackings || json

    if (newTrackings.length > 0) {
      // Prepend new records to the beginning of the array
      data.value = [...newTrackings, ...data.value]
      totalCount.value = (totalCount.value || 0) + newTrackings.length
    }
  } catch (e) {
    console.error('Auto-refresh error:', e)
  }
}

function startAutoRefresh() {
  if (autoRefreshInterval) return
  autoRefreshInterval = setInterval(fetchNewRecords, AUTO_REFRESH_INTERVAL)
}

function stopAutoRefresh() {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval)
    autoRefreshInterval = null
  }
}

// Use filtered data for charts and table display
const filteredData = computed(() => {
  return table.getFilteredRowModel().rows.map(row => row.original)
})

// Chart functions - use filtered data
function getActivityByDay() {
  if (filteredData.value.length === 0) {
    return { labels: [], data: [] }
  }

  // Find min and max dates from filtered data
  const dates = filteredData.value.map(t => t.created_at.substring(0, 10))
  const minDate = dates.reduce((a, b) => a < b ? a : b)
  const maxDate = dates.reduce((a, b) => a > b ? a : b)

  // Count activity per day
  const counts: Record<string, number> = {}
  dates.forEach(day => {
    counts[day] = (counts[day] || 0) + 1
  })

  // Generate all days in range (including days with 0 activity)
  const allDays: string[] = []
  const current = new Date(minDate)
  const end = new Date(maxDate)
  while (current <= end) {
    allDays.push(current.toISOString().substring(0, 10))
    current.setDate(current.getDate() + 1)
  }

  return {
    labels: allDays,
    data: allDays.map(d => counts[d] || 0)
  }
}

function getActivityByHour() {
  const counts: number[] = Array(24).fill(0)
  filteredData.value.forEach(t => {
    const hour = new Date(t.created_at).getHours()
    counts[hour]++
  })
  return {
    labels: counts.map((_, i) => `${i}h`),
    data: counts
  }
}

function getActionDistribution() {
  const counts: Record<string, number> = {}
  filteredData.value.forEach(t => {
    counts[t.action] = (counts[t.action] || 0) + 1
  })
  return {
    labels: Object.keys(counts),
    data: Object.values(counts)
  }
}

function toggleBarChart() {
  showBarChart.value = !showBarChart.value
  if (showBarChart.value) {
    nextTick(() => renderBarChart())
  } else if (barChartInstance) {
    barChartInstance.destroy()
    barChartInstance = null
  }
}

function toggleLineChart() {
  showLineChart.value = !showLineChart.value
  if (showLineChart.value) {
    nextTick(() => renderLineChart())
  } else if (lineChartInstance) {
    lineChartInstance.destroy()
    lineChartInstance = null
  }
}

function togglePieChart() {
  showPieChart.value = !showPieChart.value
  if (showPieChart.value) {
    nextTick(() => renderPieChart())
  } else if (pieChartInstance) {
    pieChartInstance.destroy()
    pieChartInstance = null
  }
}

function renderBarChart() {
  if (!barChartRef.value) return

  const activity = getActivityByDay()

  if (barChartInstance) {
    // Update existing chart without destroying (prevents flickering)
    barChartInstance.data.labels = activity.labels
    barChartInstance.data.datasets[0].data = activity.data
    barChartInstance.update('none') // 'none' mode skips animations for smooth update
  } else {
    barChartInstance = new Chart(barChartRef.value, {
      type: 'line',
      data: {
        labels: activity.labels,
        datasets: [{
          label: 'Activity per day',
          data: activity.data,
          fill: true,
          backgroundColor: 'rgba(74, 144, 217, 0.2)',
          borderColor: 'rgba(74, 144, 217, 1)',
          borderWidth: 2,
          tension: 0.3,
          pointBackgroundColor: 'rgba(74, 144, 217, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8
        }]
      },
      options: {
        responsive: true,
        animation: { duration: 800, easing: 'easeOutQuart' },
        scales: { y: { beginAtZero: true } }
      }
    })
  }
}

function renderLineChart() {
  if (!lineChartRef.value) return

  const activity = getActivityByHour()

  if (lineChartInstance) {
    // Update existing chart without destroying (prevents flickering)
    lineChartInstance.data.labels = activity.labels
    lineChartInstance.data.datasets[0].data = activity.data
    lineChartInstance.update('none')
  } else {
    lineChartInstance = new Chart(lineChartRef.value, {
      type: 'line',
      data: {
        labels: activity.labels,
        datasets: [{
          label: 'Activity by hour',
          data: activity.data,
          fill: true,
          backgroundColor: 'rgba(74, 144, 217, 0.2)',
          borderColor: 'rgba(74, 144, 217, 1)',
          tension: 0.4,
          pointBackgroundColor: 'rgba(74, 144, 217, 1)'
        }]
      },
      options: {
        responsive: true,
        animation: { duration: 800, easing: 'easeOutQuart' },
        scales: { y: { beginAtZero: true } }
      }
    })
  }
}

function renderPieChart() {
  if (!pieChartRef.value) return

  const dist = getActionDistribution()
  const colors = [
    'rgba(74, 144, 217, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)'
  ]

  if (pieChartInstance) {
    // Update existing chart without destroying (prevents flickering)
    pieChartInstance.data.labels = dist.labels
    pieChartInstance.data.datasets[0].data = dist.data
    pieChartInstance.data.datasets[0].backgroundColor = colors.slice(0, dist.labels.length)
    pieChartInstance.update('none')
  } else {
    pieChartInstance = new Chart(pieChartRef.value, {
      type: 'doughnut',
      data: {
        labels: dist.labels,
        datasets: [{
          data: dist.data,
          backgroundColor: colors.slice(0, dist.labels.length),
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        animation: { duration: 800, easing: 'easeOutQuart' },
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    })
  }
}

// Watch for filtered data changes and update visible charts
watch(filteredData, () => {
  if (showBarChart.value) renderBarChart()
  if (showLineChart.value) renderLineChart()
  if (showPieChart.value) renderPieChart()
})

onMounted(() => {
  fetchData()
  startAutoRefresh()
})

onUnmounted(() => {
  stopAutoRefresh()
})
</script>

<template>
  <div class="trackings-container">
    <!-- Charts Toggle -->
    <div class="charts-toggle glass">
      <label class="toggle-label">
        <input type="checkbox" @change="toggleBarChart" :checked="showBarChart" />
        <span class="toggle-text">Activity / Day</span>
      </label>
      <label class="toggle-label">
        <input type="checkbox" @change="toggleLineChart" :checked="showLineChart" />
        <span class="toggle-text">Activity / Hour</span>
      </label>
      <label class="toggle-label">
        <input type="checkbox" @change="togglePieChart" :checked="showPieChart" />
        <span class="toggle-text">Actions</span>
      </label>
      <button class="help-btn" @click="showHelpModal = true">?</button>
    </div>

    <!-- Help Modal -->
    <div v-if="showHelpModal" class="modal-overlay" @click.self="showHelpModal = false">
      <div class="modal">
        <button class="modal-close" @click="showHelpModal = false">&times;</button>
        <h2>Track Shomust - Help</h2>

        <h3>What is this page?</h3>
        <p>
          This dashboard tracks all user interactions on <strong>library.shomust.com</strong>.
          Every time a user plays, pauses, downloads, or interacts with music content,
          it gets recorded here with metadata like artist, title, album, and composer.
        </p>

        <h3>Features</h3>
        <ul>
          <li><strong>Charts:</strong> Toggle the checkboxes at the top to visualize activity per day, per hour, or action distribution.</li>
          <li><strong>Date Filter:</strong> Use the "From" and "To" fields to filter data by date range, then click "Filter".</li>
          <li><strong>Search:</strong> Type in the search box to filter across all columns instantly.</li>
          <li><strong>Hide Shoplanet:</strong> Check this box to exclude internal "shoplanet" user activity.</li>
          <li><strong>Sorting:</strong> Click any column header to sort by that column.</li>
          <li><strong>Load More:</strong> Click "Load more" to fetch additional records if available.</li>
          <li><strong>Auto-refresh:</strong> New records are automatically fetched every 5 seconds.</li>
        </ul>

        <h3>Data Columns</h3>
        <ul>
          <li><strong>When:</strong> Timestamp of the interaction</li>
          <li><strong>User:</strong> Username who performed the action</li>
          <li><strong>Action:</strong> Type of interaction (play, pause, download, etc.)</li>
          <li><strong>Composer / Album / Title / Artist:</strong> Music metadata</li>
          <li><strong>Site:</strong> Source website of the interaction</li>
        </ul>
      </div>
    </div>

    <!-- Charts Container -->
    <div v-if="showBarChart || showLineChart || showPieChart" class="charts-container">
      <div v-if="showBarChart" class="chart-wrapper glass">
        <canvas ref="barChartRef"></canvas>
      </div>
      <div v-if="showLineChart" class="chart-wrapper glass">
        <canvas ref="lineChartRef"></canvas>
      </div>
      <div v-if="showPieChart" class="chart-wrapper chart-wrapper-small glass">
        <canvas ref="pieChartRef"></canvas>
      </div>
    </div>

    <div class="filters glass">
      <div class="date-filters">
        <label>
          From:
          <input type="datetime-local" v-model="startDate" />
        </label>
        <label>
          To:
          <input type="datetime-local" v-model="endDate" />
        </label>
        <button @click="handleDateFilter" class="glass-btn filter-btn">Filter</button>
      </div>

      <div class="search-filter">
        <input
          type="text"
          v-model="globalFilter"
          placeholder="Search all columns..."
          class="glass-input"
        />
      </div>

      <label class="hide-shoplanet-toggle">
        <input type="checkbox" v-model="hideShoplanet" />
        <span>Hide Shoplanet</span>
      </label>
    </div>

    <div class="stats">
      <span>{{ filteredData.length }} tracking(s) displayed</span>
      <span v-if="totalCount > 0"> ({{ totalCount }} total)</span>
      <button
        v-if="hasMore"
        @click="loadMore"
        :disabled="loadingMore"
        class="glass-btn load-more-btn"
      >
        {{ loadingMore ? 'Loading...' : 'Load more' }}
      </button>
    </div>

    <div v-if="loading" class="loading">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else ref="tableContainerRef" class="table-container glass-table-wrap" @scroll="handleScroll">
      <table>
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :style="{ width: `${header.getSize()}px` }"
              @click="header.column.getToggleSortingHandler()?.($event)"
              :class="{ sortable: header.column.getCanSort() }"
            >
              <FlexRender
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
              <span v-if="header.column.getIsSorted()" class="sort-indicator">
                {{ header.column.getIsSorted() === 'asc' ? ' ▲' : ' ▼' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="virtualRows.length === 0 && !loading"
            class="no-data"
          >
            <td :colspan="columns.length">No trackings found</td>
          </tr>
          <template v-else>
            <tr :style="{ height: `${virtualRows[0]?.start ?? 0}px` }" />
            <tr
              v-for="virtualRow in virtualRows"
              :key="virtualRow.key"
              :style="{ height: `${virtualRow.size}px` }"
            >
              <td
                v-for="cell in table.getRowModel().rows[virtualRow.index]?.getVisibleCells()"
                :key="cell.id"
              >
                <template v-if="cell.column.id === 'actions'">
                  <a :href="`/trackings/${cell.getValue()}/edit`" class="glass-link">Edit</a>
                </template>
                <template v-else>
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="cell.getContext()"
                  />
                </template>
              </td>
            </tr>
            <tr :style="{ height: `${totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0)}px` }" />
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.trackings-container {
  font-family: 'Raleway', system-ui, sans-serif;
  color: #f0f0f0;
}

/* Charts toggle bar — layout only, glass surface via global .glass */
.charts-toggle {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1.25rem;
  border-radius: 12px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #e0e0e0;
  font-weight: 500;
  font-size: 0.85rem;
  transition: color 0.2s;
}

.toggle-label:hover {
  color: #DC2D77;
}

.toggle-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #DC2D77;
}

.toggle-text {
  letter-spacing: 0.02em;
}

/* Charts */
.charts-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Layout only — glass surface via global .glass */
.chart-wrapper {
  flex: 1;
  min-width: 300px;
  max-width: 100%;
  padding: 1.25rem;
  border-radius: 14px;
}

.chart-wrapper-small {
  max-width: 350px;
}

/* Filters — layout only, glass surface via global .glass */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.85rem 1.25rem;
  border-radius: 12px;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ddd;
  font-size: 0.85rem;
}

.date-filters label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.date-filters input {
  padding: 0.35rem 0.6rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.85rem;
  font-family: 'Raleway', sans-serif;
}

.date-filters input:focus {
  outline: none;
  border-color: rgba(220, 45, 119, 0.5);
  box-shadow: 0 0 0 2px rgba(220, 45, 119, 0.15);
}

.date-filters input:focus-visible {
  outline: 2px solid #DC2D77;
  outline-offset: 2px;
}

.search-filter {
  flex: 1;
  min-width: 200px;
}

.hide-shoplanet-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
  color: #ddd;
  font-size: 0.85rem;
}

.hide-shoplanet-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #DC2D77;
}

/* Compact size override for inline filter/load-more buttons using global .glass-btn */
.filter-btn,
.load-more-btn {
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  border-radius: 8px;
  min-height: 44px;
  min-width: 44px;
}

.glass-btn:focus-visible {
  outline: 2px solid #DC2D77;
  outline-offset: 2px;
}

.glass-btn:disabled {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.3);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.load-more-btn {
  margin-left: 1rem;
}

/* Stats */
.stats {
  margin-bottom: 0.75rem;
  color: #ccc;
  font-size: 0.85rem;
}

/* Loading/Error */
.loading {
  padding: 3rem;
  text-align: center;
  color: #ccc;
}

.error {
  padding: 2rem;
  text-align: center;
  color: #fb923c;
}

/* Table — glass surface via global .glass-table-wrap, plus scroll/fixed-layout overrides */
.table-container {
  height: 600px;
  overflow: auto;
}

table {
  table-layout: fixed;
}

thead {
  position: sticky;
  top: 0;
  z-index: 1;
  /* Material background — adapts visually to scrolling content */
  background: rgba(10, 10, 10, 0.55);
  backdrop-filter: blur(16px) saturate(120%) brightness(0.85);
  -webkit-backdrop-filter: blur(16px) saturate(120%) brightness(0.85);
}

th {
  padding: 0.75rem 0.75rem;
  border-bottom: 1px solid rgba(220, 45, 119, 0.2);
  color: #DC2D77;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

th.sortable {
  cursor: pointer;
  user-select: none;
}

th.sortable:hover {
  color: #f06;
}

.sort-indicator {
  font-size: 0.65rem;
  color: #f06;
}

td {
  padding: 0.55rem 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

tbody tr:hover td {
  color: #ffffff;
}

.no-data td {
  text-align: center;
  padding: 3rem;
  color: #999;
}

/* Edit link uses global .glass-link; add font-size override */
.glass-link {
  font-size: 0.8rem;
}

.glass-link:focus-visible {
  outline: 2px solid #DC2D77;
  outline-offset: 2px;
}

/* Help button */
.help-btn {
  margin-left: auto;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.07);
  color: #ccc;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.help-btn:hover {
  background: rgba(220, 45, 119, 0.15);
  color: #DC2D77;
  border-color: rgba(220, 45, 119, 0.3);
}

.help-btn:focus-visible {
  outline: 2px solid #DC2D77;
  outline-offset: 2px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

.modal {
  background: rgba(30, 30, 35, 0.95);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  border-radius: 50%;
  font-size: 1.2rem;
  color: #ccc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(220, 45, 119, 0.15);
  color: #DC2D77;
}

.modal-close:focus-visible {
  outline: 2px solid #DC2D77;
  outline-offset: 2px;
}

.modal h2 {
  margin: 0 0 1.5rem 0;
  color: #DC2D77;
  font-size: 1.4rem;
  font-weight: 700;
  font-family: 'Economica', sans-serif;
}

.modal h3 {
  margin: 1.5rem 0 0.75rem 0;
  color: #f0f0f0;
  font-size: 1rem;
  font-weight: 600;
}

.modal p {
  margin: 0 0 1rem 0;
  color: #ccc;
  line-height: 1.6;
  font-size: 0.9rem;
}

.modal ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #ccc;
}

.modal li {
  margin-bottom: 0.5rem;
  line-height: 1.5;
  font-size: 0.9rem;
}

.modal strong {
  color: #f0f0f0;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .date-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .date-filters label {
    flex-direction: column;
    align-items: stretch;
  }

  .search-filter {
    min-width: 0;
    width: 100%;
  }

  .charts-toggle {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .charts-container {
    flex-direction: column;
  }

  .chart-wrapper {
    min-width: 0;
    width: 100%;
  }

  .chart-wrapper-small {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    table-layout: auto;
    min-width: 800px;
  }

  .modal {
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }
}
</style>
